import { Link } from "@inertiajs/react";
import { ChevronRight } from "lucide-react";
import type { ComponentProps } from "react";

import { cn } from "@/lib/utils";

type RecordLinkProps = ComponentProps<typeof Link>;

/** A native navigation link for a named record inside a mixed-content region. */
export function RecordLink({ className, children, ...props }: RecordLinkProps) {
  return (
    <Link
      className={cn(
        "inline-flex min-h-11 min-w-0 items-center gap-2 rounded-md font-medium text-foreground underline decoration-foreground/40 underline-offset-4 outline-none transition-colors hover:bg-muted hover:decoration-foreground focus-visible:ring-3 focus-visible:ring-ring active:bg-secondary",
        className,
      )}
      {...props}
    >
      <span className="min-w-0 break-words">{children}</span>
      <ChevronRight aria-hidden="true" className="size-4 shrink-0" />
    </Link>
  );
}

/** One destination for a complete record row. Children must not contain controls. */
export function RecordRow({ className, children, ...props }: RecordLinkProps) {
  return (
    <Link
      className={cn(
        "group/record flex min-h-11 min-w-0 items-center justify-between gap-4 rounded-md px-3 py-3 text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring active:bg-secondary",
        className,
      )}
      {...props}
    >
      <div className="min-w-0 flex-1">{children}</div>
      <ChevronRight aria-hidden="true" className="size-5 shrink-0" />
    </Link>
  );
}

export function RecordTitle({ className, ...props }: ComponentProps<"span">) {
  return (
    <span
      data-record-role="identity"
      className={cn(
        "font-semibold break-words underline decoration-foreground/40 underline-offset-4 group-hover/record:decoration-foreground",
        className,
      )}
      {...props}
    />
  );
}

export function RecordDescription({
  className,
  ...props
}: ComponentProps<"p">) {
  return (
    <p
      data-record-role="description"
      className={cn(
        "text-sm leading-6 font-normal break-words text-foreground",
        className,
      )}
      {...props}
    />
  );
}

export function RecordMetadata({ className, ...props }: ComponentProps<"p">) {
  return (
    <p
      data-record-role="metadata"
      className={cn(
        "text-sm leading-5 font-normal break-words text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}
